import React from 'react'
import { Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { colors, shadows } from '../tokens'

const useStyles = makeStyles({
  panel: {
    background: '#FFFFFF',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
  },
  toggle: {
    position: 'relative',
    flex: 'none',
    width: 36,
    height: 20,
    padding: 0,
    border: 0,
    borderRadius: '999px',
    cursor: 'pointer',
    transition: 'background 120ms',
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },
  knob: {
    position: 'absolute',
    top: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0,0,0,.2)',
    transition: 'left 120ms',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    '& $toggle': {
      marginTop: 2,
    },
  },
  rowTitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
  },
  rowDesc: {
    marginTop: 2,
    fontSize: '13px',
    color: colors.muted,
  },
  heading: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.navy,
  },
  headingDesc: {
    marginTop: 4,
    fontSize: '14px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '9px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 500,
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },
  small: {
    padding: '8px 14px',
    fontSize: '13px',
  },
  primary: {
    border: 0,
    background: colors.primary,
    color: '#FFFFFF',
    '&:hover:not(:disabled)': { background: colors.primaryDark },
  },
  outline: {
    border: '1px solid rgba(1,95,251,.5)',
    background: '#FFFFFF',
    color: colors.primary,
    '&:hover:not(:disabled)': { background: 'rgba(1,95,251,.06)' },
  },
  secondary: {
    border: `1px solid ${colors.border}`,
    background: '#FFFFFF',
    color: colors.navy,
    '&:hover:not(:disabled)': { background: colors.hover },
  },
  ghost: {
    border: 0,
    background: 'transparent',
    color: colors.primary,
    '&:hover:not(:disabled)': { background: 'rgba(1,95,251,.08)' },
  },
  dark: {
    width: '100%',
    padding: '14px',
    border: 0,
    borderRadius: '10px',
    background: colors.navy,
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: 600,
    '&:hover:not(:disabled)': { background: '#001B2B' },
  },
  buttonIcon: {
    width: 14,
    height: 14,
    flex: 'none',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  input: {
    width: '100%',
    padding: '11px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    outline: 'none',
    background: '#FFFFFF',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 400,
    color: colors.navy,
    '&:focus': {
      borderColor: colors.primary,
    },
    '&[readonly]': {
      background: '#F9FAFB',
    },
  },
  note: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '12px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  noteIcon: {
    width: 14,
    height: 14,
    flex: 'none',
    marginTop: 2,
    color: colors.primary,
  },
  code: {
    margin: 0,
    padding: '16px 20px',
    borderRadius: '10px',
    background: colors.hover,
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: '13px',
    lineHeight: 1.7,
    color: colors.navy,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: 220,
    overflow: 'auto',
  },
  metric: {
    minHeight: 136,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '22px',
    padding: '20px 22px',
    background: '#FFFFFF',
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    boxShadow: shadows.card,
  },
  metricHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '15px',
    lineHeight: 1.35,
    color: colors.muted,
  },
  metricHintWrap: {
    display: 'inline-flex',
    flex: 'none',
  },
  metricHint: {
    width: 18,
    height: 18,
    flex: 'none',
    marginTop: 2,
    color: colors.subtle,
    cursor: 'help',
  },
  metricFoot: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  metricValue: {
    fontSize: '30px',
    fontWeight: 600,
    letterSpacing: '-.02em',
    lineHeight: 1,
    color: colors.navy,
  },
  metricIcon: {
    width: 26,
    height: 26,
  },
})

/** White bordered container (12px radius). */
export const Panel = ({ children, className, style }) => {
  const classes = useStyles()
  return (
    <div className={`${classes.panel} ${className || ''}`} style={style}>
      {children}
    </div>
  )
}

/** 36×20 switch. */
export const Toggle = ({ checked, onChange, disabled, title }) => {
  const classes = useStyles()
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      title={title}
      disabled={disabled}
      className={classes.toggle}
      style={{ background: checked ? colors.primary : colors.disabled }}
      onClick={event => onChange(!checked, event)}
    >
      <span className={classes.knob} style={{ left: checked ? 18 : 2 }} />
    </button>
  )
}

/** Toggle with a title and description to its right. */
export const ToggleRow = ({ title, description, checked, onChange, extra }) => {
  const classes = useStyles()
  return (
    <div className={classes.toggleRow}>
      <Toggle checked={checked} onChange={onChange} />
      <div>
        <div className={classes.rowTitle}>
          {title}
          {extra}
        </div>
        {description && <div className={classes.rowDesc}>{description}</div>}
      </div>
    </div>
  )
}

/** 18px section title with an optional description. */
export const SectionHeading = ({ title, description, children }) => {
  const classes = useStyles()
  return (
    <div>
      <div className={classes.heading}>{title}</div>
      {description && <div className={classes.headingDesc}>{description}</div>}
      {children}
    </div>
  )
}

/**
 * @param {'primary'|'outline'|'secondary'|'ghost'|'dark'} variant
 */
export const Button = ({
  variant = 'primary',
  size,
  icon,
  children,
  className,
  ...props
}) => {
  const classes = useStyles()
  return (
    <button
      type="button"
      className={[
        classes.button,
        classes[variant],
        size === 'small' && classes.small,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {icon && <Icon icon={icon} className={classes.buttonIcon} />}
      {children}
    </button>
  )
}

/** Labelled text input. */
export const TextField = ({ label, className, style, ...inputProps }) => {
  const classes = useStyles()
  const input = <input className={classes.input} {...inputProps} />
  if (!label) return input
  return (
    <label className={`${classes.field} ${className || ''}`} style={style}>
      {label}
      {input}
    </label>
  )
}

/** Small gray note with an info icon. */
export const InfoNote = ({ children, className }) => {
  const classes = useStyles()
  return (
    <div className={`${classes.note} ${className || ''}`}>
      <Icon icon="tabler:info-circle" className={classes.noteIcon} />
      <span>{children}</span>
    </div>
  )
}

export const CodeBlock = ({ children }) => {
  const classes = useStyles()
  return <pre className={classes.code}>{children}</pre>
}

/** Analytics number card. */
export const MetricCard = ({ label, hint, value, icon, iconColor }) => {
  const classes = useStyles()
  return (
    <div className={classes.metric}>
      <div className={classes.metricHead}>
        <span>{label}</span>
        {hint && (
          <Tooltip title={hint} placement="top" arrow>
            <span className={classes.metricHintWrap} aria-label={hint}>
              <Icon icon="tabler:help-circle" className={classes.metricHint} />
            </span>
          </Tooltip>
        )}
      </div>
      <div className={classes.metricFoot}>
        <span className={classes.metricValue}>{value}</span>
        <Icon
          icon={icon}
          className={classes.metricIcon}
          style={{ color: iconColor }}
        />
      </div>
    </div>
  )
}
